import {
  type Block,
  type BlockWireframe,
  Factory,
  type ID,
  type MonolithicBlock,
  ProjectActions,
  selectBlockParent,
  selectBlocksByNode,
} from '@octopus-do/octopus-project-core'
import {
  thunkUpdateSymbolColor,
  thunkUpdateSymbolTitle,
  thunkUpdateSymbolWireframes,
} from '@store/migrationActions/thunkActions/thunkSymbolEdit'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'

import { encodeAndCopyToClipboard } from '@/utils/clipboard'

export const thunkAddBlock =
  (nodeId: ID, colorId: ID, index = 0): AppThunk =>
  (dispatch) => {
    const id = getId()

    const block = Factory.block.monolithic({
      id,
      colorId,
    })

    dispatch(
      commitProjectRequest(
        ProjectActions.blocks.add({
          items: [block],
          index,
          parents: [nodeId],
        })
      )
    )

    return id
  }

export const thunkAddBlockAsSymbol =
  (ids: ID[], nodeId: ID, index: number): AppThunk =>
  (dispatch) => {
    const blocks = ids.map((symbolId) => {
      const id = getId()
      return Factory.block.symbol({
        id,
        symbolId,
      })
    })

    const depActions = blocks.map((b) =>
      ProjectActions.symbols.addDependent({ id: b.symbolId, item: b.id })
    )

    dispatch(
      commitProjectRequest(
        // todo: убрать в кор, OCT-4354
        ...depActions,
        ProjectActions.blocks.add({
          items: blocks,
          index,
          parents: [nodeId],
        })
      )
    )
  }

type UpdateSingleBlockWireframesPayload = {
  id: ID
  wireframes: BlockWireframe[]
}

export const thunkUpdateSingleBlockWireframes =
  ({ id, wireframes }: UpdateSingleBlockWireframesPayload): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const block =
      migrationProjectSelector.getProjectBlockOrSymbolData(id)(state)

    if (!block) {
      return
    }

    if (block.type === 'symbol' && block.symbolId) {
      dispatch(thunkUpdateSymbolWireframes({ id: block.symbolId, wireframes }))
      return
    }

    if (block.type === 'monolithic') {
      dispatch(
        commitProjectRequest(
          ProjectActions.blocks.updateVisual({
            ids: [id],
            visual: { wireframes, type: 'wireframes' },
          })
        )
      )
    }
  }

export const thunkChangeBlockWireframes =
  (wireframes: BlockWireframe[]): AppThunk =>
  (dispatch, getState) => {
    const { canvas } = getState()
    const selectedIds = canvas.block.ids

    if (selectedIds.length === 0) {
      return
    }

    selectedIds.forEach((id) => {
      dispatch(thunkUpdateSingleBlockWireframes({ id, wireframes }))
    })
  }

type UpdateSingleBlockColorPayload = {
  id: ID
  colorId: ID
}

const thunkUpdateSingleBlockColor =
  ({ id, colorId }: UpdateSingleBlockColorPayload): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const block =
      migrationProjectSelector.getProjectBlockOrSymbolData(id)(state)

    if (!block) {
      return
    }

    if (block.type === 'symbol' && block.symbolId) {
      dispatch(thunkUpdateSymbolColor({ id: block.symbolId, colorId }))
    } else if (block.type === 'monolithic') {
      dispatch(
        commitProjectRequest(
          ProjectActions.blocks.updateColor({
            ids: [id],
            colorId,
          })
        )
      )
    }
  }

export const thunkUpdateBlockColor =
  (colorId: ID): AppThunk =>
  (dispatch, getState) => {
    const { canvas } = getState()
    const selectedIds = canvas.block.ids

    if (selectedIds.length === 0) {
      return
    }

    selectedIds.forEach((id) => {
      dispatch(thunkUpdateSingleBlockColor({ id, colorId }))
    })
  }

export const thunkPutBlocksAfterSelectedBlock =
  (blocks: Block[]): AppThunk =>
  (dispatch, getState) => {
    const { canvas } = getState()
    const selectedBlockIds = canvas.block.ids

    if (selectedBlockIds.length === 0 || selectedBlockIds.length > 1) {
      return
    }

    const id = selectedBlockIds[0]

    const nodeId = selectBlockParent(id)(getState())[0]
    const blocksByNodeId = selectBlocksByNode(nodeId)(getState())
    const index = blocksByNodeId.indexOf(id) + 1

    dispatch(
      commitProjectRequest(
        ProjectActions.blocks.add({
          items: blocks,
          index,
          parents: [nodeId],
        })
      )
    )
  }

export const thunkCopySelectedBlock = (): AppThunk => (_, getState) => {
  const {
    manager: {
      project: { blocks },
    },
    canvas,
  } = getState()

  const selectedBlocks = canvas.block.ids.map((blockId) => ({
    id: blockId,
    type: 'block',
  }))

  const items = selectedBlocks.map((item) => {
    const block = blocks[item.id]

    let blockData = {}

    if (block.type === 'monolithic') {
      blockData = {
        type: block.type,
        title: block.title,
        colorId: block.colorId,
        visual: block.visual,
        completed: block.completed,
        content: block.content,
      }
    }

    if (block.type === 'symbol') {
      blockData = {
        type: block.type,
        symbolId: block.symbolId,
      }
    }

    return {
      ...item,
      options: {
        ...blockData,
      },
    }
  })

  encodeAndCopyToClipboard(items, 'block')
}

type ChangeBlockCompletedPayload = {
  id: ID
  completed: boolean
}

export const thunkChangeBlockCompleted =
  ({ id, completed }: ChangeBlockCompletedPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.blocks.changeCompleted({
          id,
          completed,
        })
      )
    )
  }

type UpdateBlockTitlePayload = {
  id: ID
  title: string
}

export const thunkUpdateBlockTitle =
  ({ id, title }: UpdateBlockTitlePayload): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const block =
      migrationProjectSelector.getProjectBlockOrSymbolData(id)(state)

    if (!block) {
      return
    }

    if (block.type === 'symbol' && block.symbolId) {
      dispatch(thunkUpdateSymbolTitle({ id: block.symbolId, title }))
    } else if (block.type === 'monolithic') {
      dispatch(
        commitProjectRequest(
          ProjectActions.blocks.updateTitle({
            id,
            title,
          })
        )
      )
    }
  }

export const thunkUpdateBlockTitleNoCommit =
  ({ id, title }: UpdateBlockTitlePayload): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const block =
      migrationProjectSelector.getProjectBlockOrSymbolData(id)(state)

    if (!block) {
      return
    }

    if (block.type === 'symbol' && block.symbolId) {
      dispatch(
        ProjectActions.symbols.updateTitle({
          id: block.symbolId,
          title,
        })
      )
    } else if (block.type === 'monolithic') {
      dispatch(
        ProjectActions.blocks.updateTitle({
          id,
          title,
        })
      )
    }
  }

export const thunkConvertBlockToSymbol =
  (blockId: ID): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const block =
      migrationProjectSelector.getProjectBlockOrSymbolData(blockId)(state)

    if (!block || block.type !== 'monolithic') {
      return
    }

    const symbolId = getId()

    const symbolData = Factory.block.extractableData({
      colorId: block.colorId,
      title: block.title,
      content: block.content,
      visual: (block as MonolithicBlock).visual,
    })

    const symbol = Factory.symbol({
      id: symbolId,
      data: symbolData,
    })

    dispatch(
      commitProjectRequest(
        ProjectActions.symbols.add({
          items: [symbol],
        }),
        ProjectActions.blocks.linkSymbol({
          id: blockId,
          symbolId,
        })
      )
    )

    return symbolId
  }

export const thunkUnlinkSymbol =
  (blockId: ID): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const block =
      migrationProjectSelector.getProjectBlockOrSymbolData(blockId)(state)

    if (!block || block.type !== 'symbol' || !block.symbolId) {
      return
    }

    const symbol = migrationProjectSelector.getProjectSymbolById(
      block.symbolId
    )(state)

    if (!symbol) {
      return
    }

    // The block takes the data the symbol renders with — for a library ref
    // that is the data of the library item.
    const data = migrationProjectSelector.getProjectSymbolData(block.symbolId)(
      state
    )

    dispatch(
      // todo: убрать в кор, OCT-4354
      commitProjectRequest(
        ProjectActions.symbols.removeDependent({
          id: block.symbolId,
          item: blockId,
        }),
        ProjectActions.blocks.unlinkSymbol({
          ids: [blockId],
          data,
        })
      )
    )
  }
